import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const CualinmarketComponent = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/listings`);
      const oficialListings = (res.data.listings || []).filter(l => l.is_tienda_cualapp);
      setListings(oficialListings);
    } catch (err) {
      console.error('Error loading CUALINMARKET:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = async (listing, paymentMethod) => {
    setBuying(listing.id);
    try {
      const res = await axios.post(`${API}/marketplace/listings/${listing.id}/buy`, { payment_method: paymentMethod });
      alert(res.data.message);
      fetchListings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al comprar');
    } finally {
      setBuying(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="cualinmarket-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-3 mb-2">
          <span className="text-3xl">🏪</span>
          <h2 className="text-2xl font-bold text-gray-900">CUALINMARKET</h2>
        </div>
        <p className="text-gray-600">Tienda oficial de CUALAPP — Compra directo de la plataforma</p>
      </div>

      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🔒</span>
          <div>
            <h3 className="font-semibold text-amber-800">Compra con confianza</h3>
            <p className="text-sm text-amber-700 mt-1">
              CUALINMARKET es la tienda oficial de CUALAPP. Todos los productos aqui son ofrecidos directamente por la plataforma.
              Sin intermediarios, sin riesgo de fraude, con garantia total.
            </p>
          </div>
        </div>
      </div>

      {listings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <span className="text-5xl block mb-4">🏪</span>
          <h3 className="text-lg font-semibold text-gray-700">CUALINMARKET esta preparando su catalogo</h3>
          <p className="text-gray-500 mt-2">Pronto habra productos y servicios oficiales de CUALAPP disponibles aqui.</p>
          <p className="text-sm text-gray-400 mt-4">Mientras tanto, visita el <strong>Marketplace</strong> para comprar y vender entre usuarios.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {listings.map(listing => (
            <div key={listing.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`cualinmarket-listing-${listing.id}`}>
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">OFICIAL</span>
                  <span className="text-white/80 text-xs">CUALINMARKET</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-gray-900 text-lg">{listing.title}</h3>
                {listing.description && (
                  <p className="text-gray-600 text-sm mt-1">{listing.description}</p>
                )}
                <div className="mt-4 flex items-center gap-4">
                  <div className="bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                    <span className="text-xs text-green-600 block">Precio USD</span>
                    <span className="font-bold text-green-700">${listing.price_usd}</span>
                  </div>
                  <div className="bg-purple-50 rounded-lg px-3 py-2 border border-purple-200">
                    <span className="text-xs text-purple-600 block">Precio CUALCOIN</span>
                    <span className="font-bold text-purple-700">{listing.price_cualcoin} CUAL</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => handleBuy(listing, 'USD')}
                    disabled={buying === listing.id || listing.is_mine}
                    data-testid={`cualinmarket-buy-usd-${listing.id}`}
                    className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {buying === listing.id ? 'Procesando...' : 'Comprar con USD'}
                  </button>
                  <button
                    onClick={() => handleBuy(listing, 'CUALCOIN')}
                    disabled={buying === listing.id || listing.is_mine}
                    data-testid={`cualinmarket-buy-cual-${listing.id}`}
                    className="flex-1 py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {buying === listing.id ? 'Procesando...' : 'Comprar con CUALCOIN'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-3">Diferencia entre Marketplace y CUALINMARKET</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-800 flex items-center gap-2"><span>🤝</span> Marketplace</h4>
            <p className="text-sm text-gray-600 mt-1">Mercado libre entre usuarios. Tu vendes, tu compras. CUALAPP solo arbitra disputas.</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <h4 className="font-medium text-purple-800 flex items-center gap-2"><span>🏪</span> CUALINMARKET</h4>
            <p className="text-sm text-gray-600 mt-1">Tienda oficial de CUALAPP. Compras directamente a la plataforma con garantia total.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CualinmarketComponent;
